'use client';
import React, { useState } from 'react';
import { CheckCircle, AlertTriangle, XCircle, RefreshCw, Download, Eye, Zap, ChevronDown, ChevronRight, Clock, Bot, User, Filter, AlertCircle, FileText, ArrowLeftRight, Lock } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionHeader from '@/components/ui/SectionHeader';

// Backend integration: fetch from /api/v1/finance/reconciliation/summary
const reconMetrics = {
  totalBatches: 47,
  automatedBatches: 39,
  manualBatches: 8,
  fullyReconciled: 43,
  discrepancies: 4,
  pendingReview: 6,
  totalDiscrepancyValue: '$127,440',
  automationRate: '82.9%',
};

// Backend integration: fetch from /api/v1/finance/reconciliation/discrepancy-alerts
const discrepancyAlerts = [
  {
    id: 'disc-001',
    severity: 'critical' as const,
    batchRef: 'SB-20260820-004',
    campaign: 'ClearPath Clean Energy Fund III',
    type: 'Amount Mismatch',
    expected: '$7,840,000',
    actual: '$7,812,560',
    variance: '-$27,440',
    variancePct: '-0.35%',
    detectedAt: '2026-08-20 09:14',
    status: 'unresolved',
    assignedTo: 'J. Harrington',
  },
  {
    id: 'disc-002',
    severity: 'warning' as const,
    batchRef: 'SB-20260819-003',
    campaign: 'Meridian Logistics Hub — Series A',
    type: 'Investor Count Mismatch',
    expected: '28 investors',
    actual: '27 investors',
    variance: '-1 investor',
    variancePct: '-3.57%',
    detectedAt: '2026-08-19 15:02',
    status: 'under-review',
    assignedTo: 'M. Okonkwo',
  },
  {
    id: 'disc-003',
    severity: 'warning' as const,
    batchRef: 'SB-20260818-002',
    campaign: 'Thornfield RE Fund II — Distribution',
    type: 'Timing Discrepancy',
    expected: '2026-08-18 12:00',
    actual: '2026-08-18 14:37',
    variance: '+2h 37m',
    variancePct: 'N/A',
    detectedAt: '2026-08-18 14:40',
    status: 'resolved',
    assignedTo: 'System',
  },
  {
    id: 'disc-004',
    severity: 'critical' as const,
    batchRef: 'SB-20260817-001',
    campaign: 'Vantage Credit Opp. Fund — Refund',
    type: 'Bank Rejection',
    expected: '$45,000 credited',
    actual: 'Bank rejected — code R03',
    variance: '$45,000 unreconciled',
    variancePct: '100%',
    detectedAt: '2026-08-17 16:22',
    status: 'escalated',
    assignedTo: 'Finance Director',
  },
];

// Backend integration: fetch from /api/v1/finance/reconciliation/batches
const settlementBatches = [
  {
    id: 'batch-001',
    batchRef: 'SB-20260820-004',
    campaign: 'ClearPath Clean Energy Fund III',
    method: 'automated' as const,
    totalAmount: '$7,840,000',
    investors: 198,
    submittedAt: '2026-08-20 09:00',
    reconciledAt: null,
    status: 'discrepancy' as const,
    ledgerMatch: false,
    escrowMatch: false,
    bankConfirmed: false,
    discrepancyCount: 1,
    notes: 'Amount variance detected — $27,440 shortfall vs escrow balance',
  },
  {
    id: 'batch-002',
    batchRef: 'SB-20260819-003',
    campaign: 'Meridian Logistics Hub — Series A',
    method: 'automated' as const,
    totalAmount: '$2,100,000',
    investors: 28,
    submittedAt: '2026-08-19 14:30',
    reconciledAt: '2026-08-19 14:58',
    status: 'partial' as const,
    ledgerMatch: true,
    escrowMatch: true,
    bankConfirmed: false,
    discrepancyCount: 1,
    notes: 'Investor count mismatch — 1 investor record missing from batch manifest',
  },
  {
    id: 'batch-003',
    batchRef: 'SB-20260818-002',
    campaign: 'Thornfield RE Fund II — Distribution',
    method: 'manual' as const,
    totalAmount: '$287,440',
    investors: 142,
    submittedAt: '2026-08-18 11:00',
    reconciledAt: '2026-08-18 15:10',
    status: 'reconciled' as const,
    ledgerMatch: true,
    escrowMatch: true,
    bankConfirmed: true,
    discrepancyCount: 0,
    notes: 'Timing discrepancy resolved — manual override applied by J. Harrington',
  },
  {
    id: 'batch-004',
    batchRef: 'SB-20260817-001',
    campaign: 'Vantage Credit Opp. Fund — Refund',
    method: 'automated' as const,
    totalAmount: '$45,000',
    investors: 3,
    submittedAt: '2026-08-17 16:22',
    reconciledAt: null,
    status: 'failed' as const,
    ledgerMatch: false,
    escrowMatch: false,
    bankConfirmed: false,
    discrepancyCount: 1,
    notes: 'Bank rejection R03 — account closed. Moved to suspense. Escalated to Finance Director.',
  },
  {
    id: 'batch-005',
    batchRef: 'SB-20260816-007',
    campaign: 'Ashford Commercial REIT I — Q2 Distribution',
    method: 'automated' as const,
    totalAmount: '$412,000',
    investors: 87,
    submittedAt: '2026-08-16 10:00',
    reconciledAt: '2026-08-16 10:22',
    status: 'reconciled' as const,
    ledgerMatch: true,
    escrowMatch: true,
    bankConfirmed: true,
    discrepancyCount: 0,
    notes: 'Fully reconciled — automated 3-way match passed',
  },
  {
    id: 'batch-006',
    batchRef: 'SB-20260815-006',
    campaign: 'Vantage Private Credit Fund I — Subscription',
    method: 'manual' as const,
    totalAmount: '$4,500,000',
    investors: 34,
    submittedAt: '2026-08-15 13:00',
    reconciledAt: '2026-08-15 17:45',
    status: 'reconciled' as const,
    ledgerMatch: true,
    escrowMatch: true,
    bankConfirmed: true,
    discrepancyCount: 0,
    notes: 'Manual reconciliation — custodian statement matched. Approved by Finance Officer.',
  },
];

const batchStatusConfig = {
  reconciled: { label: 'Reconciled', variant: 'confirmed' as const, color: 'text-positive', bg: 'bg-positive/10' },
  partial: { label: 'Partial Match', variant: 'review' as const, color: 'text-warning', bg: 'bg-warning/10' },
  discrepancy: { label: 'Discrepancy', variant: 'edd' as const, color: 'text-critical', bg: 'bg-critical/10' },
  failed: { label: 'Failed', variant: 'failed' as const, color: 'text-negative', bg: 'bg-negative/10' },
  pending: { label: 'Pending', variant: 'pending' as const, color: 'text-muted-foreground', bg: 'bg-surface-3' },
};

const severityConfig = {
  critical: { color: 'text-critical', bg: 'bg-critical/10 border-critical/20', dot: 'bg-critical', label: 'Critical' },
  warning: { color: 'text-warning', bg: 'bg-warning/10 border-warning/20', dot: 'bg-warning', label: 'Warning' },
  info: { color: 'text-info', bg: 'bg-info/10 border-info/20', dot: 'bg-info', label: 'Info' },
};

export default function ReconciliationDashboard() {
  const [activeTab, setActiveTab] = useState<'summary' | 'batches' | 'alerts'>('summary');
  const [expandedBatch, setExpandedBatch] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const filteredBatches = filterStatus === 'all'
    ? settlementBatches
    : settlementBatches.filter(b => b.status === filterStatus);

  const unresolvedAlerts = discrepancyAlerts.filter(a => a.status !== 'resolved').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Reconciliation Centre</h1>
          <p className="text-sm text-muted-foreground mt-1">Automated & Manual · Discrepancy Alerts · Batch Summary · 20 Aug 2026, 16:12 UTC</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary text-xs gap-1.5"><RefreshCw size={12} /> Refresh</button>
          <button className="btn-primary text-xs gap-1.5"><Download size={12} /> Export Report</button>
        </div>
      </div>

      {/* Unresolved discrepancy banner */}
      {unresolvedAlerts > 0 && (
        <div className="alert-banner alert-critical">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-sm">{unresolvedAlerts} Unresolved Discrepancies Require Immediate Attention</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Total variance exposure: <span className="font-mono-data font-bold text-foreground">{reconMetrics.totalDiscrepancyValue}</span> across {unresolvedAlerts} settlement batches. Review and resolve before next settlement cycle.
            </p>
          </div>
          <button
            onClick={() => setActiveTab('alerts')}
            className="flex-shrink-0 btn-ghost text-xs gap-1 text-critical"
          >
            View Alerts <ChevronRight size={11} />
          </button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Automation Rate"
          value={reconMetrics.automationRate}
          subValue={`${reconMetrics.automatedBatches} of ${reconMetrics.totalBatches} batches`}
          variant="positive"
          icon={Bot}
          iconColor="text-positive"
        />
        <MetricCard
          label="Manual Batches"
          value={String(reconMetrics.manualBatches)}
          subValue="Require human review"
          variant="warning"
          icon={User}
          iconColor="text-warning"
        />
        <MetricCard
          label="Active Discrepancies"
          value={String(reconMetrics.discrepancies)}
          subValue={`Total variance: ${reconMetrics.totalDiscrepancyValue}`}
          variant="critical"
          icon={AlertCircle}
          iconColor="text-critical"
        />
        <MetricCard
          label="Fully Reconciled"
          value={String(reconMetrics.fullyReconciled)}
          subValue={`${reconMetrics.pendingReview} pending review`}
          variant="default"
          icon={CheckCircle}
          iconColor="text-accent"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border overflow-x-auto">
        {([
          { key: 'summary', label: 'Reconciliation Summary' },
          { key: 'batches', label: 'Batch Status', badge: reconMetrics.pendingReview },
          { key: 'alerts', label: 'Discrepancy Alerts', badge: unresolvedAlerts, badgeVariant: 'critical' as const },
        ]).map((tab) => (
          <button
            key={`recon-tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key as any)}
            className={`portal-tab ${activeTab === tab.key ? 'active' : ''}`}
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className={`ml-1.5 text-xs rounded-full px-1.5 py-0.5 font-mono-data ${
                tab.badgeVariant === 'critical' ? 'bg-critical/20 text-critical' : 'bg-warning/20 text-warning'
              }`}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Summary Tab */}
      {activeTab === 'summary' && (
        <div className="space-y-6">
          {/* Automated vs Manual breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Automated */}
            <div className="glass-card rounded-xl p-5 border-l-4 border-l-positive">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-positive/10 flex items-center justify-center">
                  <Bot size={18} className="text-positive" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">Automated Reconciliation</p>
                  <p className="text-xs text-muted-foreground">System-driven 3-way match engine</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-2xl font-bold font-mono-data text-foreground">{reconMetrics.automatedBatches}</p>
                  <p className="text-xs text-muted-foreground">batches</p>
                </div>
              </div>
              <div className="space-y-2.5">
                {[
                  { label: 'Ledger ↔ Escrow Match', passed: 37, total: 39 },
                  { label: 'Escrow ↔ Bank Confirmation', passed: 35, total: 39 },
                  { label: 'Investor Count Validation', passed: 38, total: 39 },
                  { label: 'Amount Tolerance Check (±0.01%)', passed: 36, total: 39 },
                ].map((check) => (
                  <div key={check.label} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">{check.label}</span>
                        <span className="text-xs font-mono-data text-foreground">{check.passed}/{check.total}</span>
                      </div>
                      <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${check.passed === check.total ? 'bg-positive' : 'bg-warning'}`}
                          style={{ width: `${(check.passed / check.total) * 100}%` }}
                        />
                      </div>
                    </div>
                    {check.passed === check.total
                      ? <CheckCircle size={14} className="text-positive flex-shrink-0" />
                      : <AlertTriangle size={14} className="text-warning flex-shrink-0" />
                    }
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><Zap size={11} className="text-positive" /> Avg reconciliation time: <span className="font-mono-data text-foreground">4m 12s</span></span>
                <span className="flex items-center gap-1.5"><Clock size={11} /> Last run: <span className="font-mono-data text-foreground">09:14 UTC</span></span>
              </div>
            </div>

            {/* Manual */}
            <div className="glass-card rounded-xl p-5 border-l-4 border-l-warning">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-warning/10 flex items-center justify-center">
                  <User size={18} className="text-warning" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">Manual Reconciliation</p>
                  <p className="text-xs text-muted-foreground">Finance Officer review & override</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-2xl font-bold font-mono-data text-foreground">{reconMetrics.manualBatches}</p>
                  <p className="text-xs text-muted-foreground">batches</p>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Awaiting Finance Officer Review', count: 3, color: 'text-warning', bg: 'bg-warning/10' },
                  { label: 'Escalated to Finance Director', count: 1, color: 'text-critical', bg: 'bg-critical/10' },
                  { label: 'Manual Override Applied', count: 2, color: 'text-info', bg: 'bg-info/10' },
                  { label: 'Completed — Manual Sign-off', count: 2, color: 'text-positive', bg: 'bg-positive/10' },
                ].map((item) => (
                  <div key={item.label} className={`flex items-center justify-between px-3 py-2 rounded-lg ${item.bg}`}>
                    <span className="text-xs text-muted-foreground">{item.label}</span>
                    <span className={`text-sm font-bold font-mono-data ${item.color}`}>{item.count}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><Clock size={11} /> Avg resolution time: <span className="font-mono-data text-foreground">3h 28m</span></span>
                <span className="flex items-center gap-1.5"><Lock size={11} /> Requires dual sign-off</span>
              </div>
            </div>
          </div>

          {/* Settlement Batch Reconciliation Summary Table */}
          <div className="glass-card rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <SectionHeader title="Settlement Batch Reconciliation Summary" subtitle="3-way match status: Ledger · Escrow · Bank" />
              <button className="btn-ghost text-xs gap-1.5"><Download size={13} /> Export</button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-3">
                  <tr>
                    {['Batch Ref', 'Campaign', 'Method', 'Amount', 'Ledger', 'Escrow', 'Bank', 'Status', 'Discrepancies'].map((h) => (
                      <th key={`th-recon-${h}`} className="table-th">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {settlementBatches.map((batch) => {
                    const cfg = batchStatusConfig[batch.status];
                    return (
                      <tr key={batch.id} className={`data-table-row ${batch.status === 'discrepancy' || batch.status === 'failed' ? 'bg-critical/3' : ''}`}>
                        <td className="table-td font-mono-data text-xs text-accent">{batch.batchRef}</td>
                        <td className="table-td text-sm text-foreground max-w-[180px]">
                          <p className="truncate">{batch.campaign}</p>
                        </td>
                        <td className="table-td">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                            batch.method === 'automated' ? 'bg-positive/10 text-positive' : 'bg-warning/10 text-warning'
                          }`}>
                            {batch.method === 'automated' ? <Bot size={10} /> : <User size={10} />}
                            {batch.method === 'automated' ? 'Auto' : 'Manual'}
                          </span>
                        </td>
                        <td className="table-td font-mono-data font-bold text-foreground text-sm">{batch.totalAmount}</td>
                        <td className="table-td text-center">
                          {batch.ledgerMatch
                            ? <CheckCircle size={14} className="text-positive mx-auto" />
                            : <XCircle size={14} className="text-negative mx-auto" />}
                        </td>
                        <td className="table-td text-center">
                          {batch.escrowMatch
                            ? <CheckCircle size={14} className="text-positive mx-auto" />
                            : <XCircle size={14} className="text-negative mx-auto" />}
                        </td>
                        <td className="table-td text-center">
                          {batch.bankConfirmed
                            ? <CheckCircle size={14} className="text-positive mx-auto" />
                            : <XCircle size={14} className="text-negative mx-auto" />}
                        </td>
                        <td className="table-td">
                          <StatusBadge variant={cfg.variant} label={cfg.label} dot />
                        </td>
                        <td className="table-td text-center">
                          {batch.discrepancyCount > 0
                            ? <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-critical/20 text-critical text-xs font-bold font-mono-data">{batch.discrepancyCount}</span>
                            : <span className="text-muted-foreground text-xs">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Batches Tab */}
      {activeTab === 'batches' && (
        <div className="space-y-4">
          {/* Filter bar */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Filter:</span>
            {(['all', 'reconciled', 'partial', 'discrepancy', 'failed'] as const).map((f) => (
              <button
                key={`filter-${f}`}
                onClick={() => setFilterStatus(f)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                  filterStatus === f
                    ? 'bg-accent text-white border-accent' :'bg-surface-3 text-muted-foreground border-border hover:border-accent/40'
                }`}
              >
                {f === 'all' ? 'All Batches' : batchStatusConfig[f].label}
              </button>
            ))}
          </div>

          {filteredBatches.map((batch) => {
            const cfg = batchStatusConfig[batch.status];
            const isExpanded = expandedBatch === batch.id;
            return (
              <div key={batch.id} className={`glass-card rounded-xl overflow-hidden ${
                batch.status === 'discrepancy' || batch.status === 'failed' ? 'border border-critical/20' : ''
              }`}>
                <div
                  className="px-4 py-4 cursor-pointer hover:bg-surface-3/30 transition-colors"
                  onClick={() => setExpandedBatch(isExpanded ? null : batch.id)}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <p className="font-mono-data text-xs text-accent">{batch.batchRef}</p>
                        <StatusBadge variant={cfg.variant} label={cfg.label} dot />
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                          batch.method === 'automated' ? 'bg-positive/10 text-positive' : 'bg-warning/10 text-warning'
                        }`}>
                          {batch.method === 'automated' ? <Bot size={10} /> : <User size={10} />}
                          {batch.method === 'automated' ? 'Automated' : 'Manual'}
                        </span>
                        {batch.discrepancyCount > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-critical/10 text-critical">
                            <AlertTriangle size={10} /> {batch.discrepancyCount} discrepancy
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-foreground mb-1">{batch.campaign}</p>
                      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                        <span>Amount: <span className="font-mono-data font-bold text-foreground">{batch.totalAmount}</span></span>
                        <span>Investors: <span className="font-mono-data">{batch.investors}</span></span>
                        <span>Submitted: <span className="font-mono-data">{batch.submittedAt}</span></span>
                        {batch.reconciledAt && <span>Reconciled: <span className="font-mono-data text-positive">{batch.reconciledAt}</span></span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {/* 3-way match indicators */}
                      <div className="flex items-center gap-1.5">
                        {[
                          { label: 'L', title: 'Ledger', ok: batch.ledgerMatch },
                          { label: 'E', title: 'Escrow', ok: batch.escrowMatch },
                          { label: 'B', title: 'Bank', ok: batch.bankConfirmed },
                        ].map((check) => (
                          <div
                            key={check.label}
                            title={`${check.title}: ${check.ok ? 'Matched' : 'Not matched'}`}
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              check.ok ? 'bg-positive/20 text-positive' : 'bg-negative/20 text-negative'
                            }`}
                          >
                            {check.label}
                          </div>
                        ))}
                      </div>
                      <button className="btn-ghost p-1.5"><Eye size={13} /></button>
                      {isExpanded ? <ChevronDown size={14} className="text-muted-foreground" /> : <ChevronRight size={14} className="text-muted-foreground" />}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-border bg-surface-3/20">
                    <div className="pt-3 space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: 'Ledger Match', ok: batch.ledgerMatch, desc: batch.ledgerMatch ? 'Debit/credit entries balanced' : 'Ledger entry mismatch detected' },
                          { label: 'Escrow Match', ok: batch.escrowMatch, desc: batch.escrowMatch ? 'Escrow balance confirmed' : 'Escrow balance variance found' },
                          { label: 'Bank Confirmed', ok: batch.bankConfirmed, desc: batch.bankConfirmed ? 'Bank statement matched' : 'Awaiting bank confirmation' },
                        ].map((check) => (
                          <div key={check.label} className={`p-3 rounded-lg border ${check.ok ? 'bg-positive/5 border-positive/20' : 'bg-negative/5 border-negative/20'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              {check.ok ? <CheckCircle size={13} className="text-positive" /> : <XCircle size={13} className="text-negative" />}
                              <span className="text-xs font-semibold text-foreground">{check.label}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">{check.desc}</p>
                          </div>
                        ))}
                      </div>
                      {batch.notes && (
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-surface-3 border border-border">
                          <FileText size={13} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-muted-foreground">{batch.notes}</p>
                        </div>
                      )}
                      <div className="flex items-center gap-2 justify-end">
                        {(batch.status === 'discrepancy' || batch.status === 'partial') && (
                          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-warning/15 text-warning hover:bg-warning/25 border border-warning/20 transition-colors">
                            <User size={12} /> Assign for Manual Review
                          </button>
                        )}
                        {batch.status === 'failed' && (
                          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-info/15 text-info hover:bg-info/25 border border-info/20 transition-colors">
                            <RefreshCw size={12} /> Retry Reconciliation
                          </button>
                        )}
                        <button className="btn-ghost text-xs gap-1.5"><Download size={12} /> Download Proof</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          <SectionHeader
            title="Discrepancy Alerts"
            subtitle={`${unresolvedAlerts} unresolved · Total variance: ${reconMetrics.totalDiscrepancyValue}`}
          />
          {discrepancyAlerts.map((alert) => {
            const sev = severityConfig[alert.severity];
            return (
              <div key={alert.id} className={`glass-card rounded-xl p-4 border ${sev.bg}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${sev.dot}`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className={`text-xs font-bold uppercase tracking-wider ${sev.color}`}>{sev.label}</span>
                        <span className="font-mono-data text-xs text-accent">{alert.batchRef}</span>
                        <span className="text-xs font-medium text-foreground bg-surface-3 px-2 py-0.5 rounded">{alert.type}</span>
                        <StatusBadge
                          variant={alert.status === 'resolved' ? 'confirmed' : alert.status === 'escalated' ? 'failed' : alert.status === 'under-review' ? 'review' : 'pending'}
                          label={alert.status.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          dot
                        />
                      </div>
                      <p className="text-sm font-semibold text-foreground mb-2">{alert.campaign}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
                        {[
                          { label: 'Expected', value: alert.expected },
                          { label: 'Actual', value: alert.actual },
                          { label: 'Variance', value: alert.variance, highlight: true },
                          { label: 'Variance %', value: alert.variancePct, highlight: true },
                        ].map((item) => (
                          <div key={item.label} className="bg-surface-3/60 rounded-lg px-3 py-2">
                            <p className="text-xs text-muted-foreground mb-0.5">{item.label}</p>
                            <p className={`text-xs font-mono-data font-bold ${item.highlight ? sev.color : 'text-foreground'}`}>{item.value}</p>
                          </div>
                        ))}
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                        <span>Detected: <span className="font-mono-data">{alert.detectedAt}</span></span>
                        <span>Assigned to: <span className="text-foreground font-medium">{alert.assignedTo}</span></span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {alert.status !== 'resolved' && (
                      <>
                        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-positive/15 text-positive hover:bg-positive/25 border border-positive/20 transition-colors">
                          <CheckCircle size={12} /> Resolve
                        </button>
                        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-warning/15 text-warning hover:bg-warning/25 border border-warning/20 transition-colors">
                          <ArrowLeftRight size={12} /> Escalate
                        </button>
                      </>
                    )}
                    <button className="btn-ghost p-1.5"><Eye size={13} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
